/**
 * Vault client state (zustand). Holds the lock status, the plaintext item
 * envelopes (never secret data — decrypted payloads are fetched on demand by
 * the detail pane and kept in component state only), and the sidebar's
 * category/search selection. All persistence lives Rust-side behind the
 * vault_* commands (see plans/25_07_2026/vault-suite/CONTRACTS.md §1); this
 * store is a thin cache over vault_status + vault_list.
 */
import { create } from 'zustand';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import { ensureE2eKeypair, resetE2eKeypairCache } from '../utils/vaultShareCrypto';
import { ensureVaultSync } from '../services/vaultSync';
import { addSessionEndedListener } from '../services/channelService';
// Pure, store-free by design (see that module's header) — importing it here
// cannot pull `teamVaultStore` into this module's dependency graph, and team
// items still never touch this store.
import { isTeamCategory } from '../utils/teamVaultSections';
import { requireLocalCapacity } from '../plans/localGate';
import { capRefusal, isCapped } from '../plans/capRefusal';
import i18n from '../i18n';

const initialState = {
  // { initialized, unlocked, itemCount, protected, remembered } | null
  // (unknown / not yet queried). `protected` and `remembered` are additive
  // fields from the master-passphrase phase; a build that predates them reads
  // them as undefined, which is the same as "not protected, not remembered".
  status: null,
  // Envelopes only: { id, type, name, rev, createdAt, updatedAt, sync,
  // favorite, index } — no secret values.
  items: [],
  loading: false,
  error: null,
  // 'all' | 'favorites' | 'password' | 'sftp' | 'dbConnection' | 'authHeader' | 'note' | 'custom'
  activeCategory: 'all',
  search: '',
  selectedItemId: null,
  // True after an explicit user "Lock vault". While set, mount-time /
  // panel-enter auto-unlock is skipped so re-entering the panel does not
  // silently undo the lock; cleared by an explicit unlock (ensureUnlocked).
  userLocked: false,
};

const errText = (e) => String(e?.message || e || 'Vault error');

/**
 * The bare token Rust returned, or ''.
 *
 * `vault.rs` returns unprefixed tokens as bare `String`s (`passphrase_required`,
 * `bad_passphrase`, `vault_locked`, …) which arrive here either as a string or
 * wrapped in an Error. Everything else — "vault task failed: …", an IO message —
 * is not a token and must not be pattern-matched as one.
 */
export function vaultErrorToken(e) {
  const raw = typeof e === 'string' ? e : String(e?.message || e || '');
  return /^[a-z][a-z0-9_]*$/.test(raw.trim()) ? raw.trim() : '';
}

/**
 * The one interface between the four `vault_unlock` callers and the always-
 * mounted passphrase gate (CONTRACT §6.1, §7). Nothing else is agreed between
 * them: the callers dispatch, the gate listens, and neither imports the other.
 */
export const VAULT_GATE_EVENT = 'vault-gate-open';

/** Fired by the gate after a successful unlock, so callers can retry. */
export const VAULT_GATE_UNLOCKED_EVENT = 'vault-gate-unlocked';

/**
 * Fired by the gate when it closes WITHOUT an unlock — the user pressed
 * Escape, clicked away, or cancelled. The pair lets a caller that opened the
 * gate to finish an operation (e.g. "save this connection to the vault"
 * while locked) resolve either way instead of waiting forever on a modal the
 * user already dismissed.
 */
export const VAULT_GATE_DISMISSED_EVENT = 'vault-gate-dismissed';

/**
 * Fired by `onVaultLocked` — the single frontend funnel every lock runs
 * through (manual button, logout, and the Rust `vault:locked` event alike).
 * Surfaces that CACHE vault-derived state listen to this to flip their locked
 * indicators immediately; the DB Manager guest is the prime case, since it is
 * a keep-alive iframe that otherwise re-reads only on activation.
 */
export const VAULT_LOCKED_UI_EVENT = 'vault-locked-ui';

/**
 * Fired by `onVaultUnlocked` — the mirror of VAULT_LOCKED_UI_EVENT, and the
 * one every surface that CACHES vault-derived state should listen to.
 *
 * It exists because locking had a funnel and unlocking did not. The only
 * unlock announcement was VAULT_GATE_UNLOCKED_EVENT, dispatched from the
 * passphrase gate alone — so every unlock that did not go through the modal
 * (the `vault_unlock`-with-no-secret fast path used by the vault panel, the
 * FTP sidebar, the chat share card, and the DB Manager's own Unlock button)
 * left those caches believing the vault was still locked.
 *
 * The DB Manager showed it worst: a keep-alive iframe holding a private
 * `vaultLocked` boolean, refreshed only by a host push. Clicking its Unlock
 * row DID unlock the vault and then announced nothing, so the row never
 * cleared and every further click repeated the same silent success — the tree
 * appeared frozen until a restart.
 *
 * Distinct from VAULT_GATE_UNLOCKED_EVENT on purpose. That one means "the gate
 * modal finished", and callers waiting to retry a deferred operation need
 * exactly that. This one means "the vault is open, however it happened".
 */
export const VAULT_UNLOCKED_UI_EVENT = 'vault-unlocked-ui';

/** Ask the gate to open. `reason` is a free-form tag used only for the subtitle. */
export function openVaultGate(reason) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(VAULT_GATE_EVENT, { detail: { reason: reason || 'vault' } }));
}

// Internal items (e.g. the "__e2e_keypair__" chat-share keypair) are hidden
// from the UI by the "__" name-prefix convention (CONTRACTS.md §3).
const visibleItems = (list) =>
  (Array.isArray(list) ? list : []).filter((it) => !String(it?.name || '').startsWith('__'));

/** Plans v2: vault item types that count against a local cap. */
const LOCAL_KIND_OF_VAULT_TYPE = { dbConnection: 'dbConnections', sftp: 'hosts' };

const planLimitError = (message) => Object.assign(new Error(message || 'plan_limit'), { planLimit: true });

/**
 * Plans v2: THE check before writing NEW vault items (`vault_put_item`
 * without an id): the item modal (putItem), a bulk import
 * (VaultImportExportModal) and "Save to vault" from chat
 * (VaultShareSaveModal) all call it. dbConnection / sftp items past the
 * plan's cap are refused as a whole batch (`items` may hold several; an item
 * with an id is an edit and never counts).
 *
 * Counts from a fresh vault_list (the panel's list may never have loaded,
 * e.g. from the FTP panel's "New connection"), and only when the plan caps
 * the kind. A vault that cannot be listed is NOT room: the write is refused
 * with a sentence saying so. Refused past the cap, the upgrade opens and the
 * error carries the sentence (shown inline by the callers).
 */
export async function guardNewVaultItems(items) {
  const adding = new Map();
  for (const item of Array.isArray(items) ? items : [items]) {
    const kind = !item?.id ? LOCAL_KIND_OF_VAULT_TYPE[item?.type] : null;
    if (kind && isCapped(kind)) adding.set(item.type, (adding.get(item.type) || 0) + 1);
  }
  if (adding.size === 0) return;
  let list;
  try {
    list = await invoke('vault_list');
  } catch {
    list = null;
  }
  if (!Array.isArray(list)) throw planLimitError(i18n.t('plans.localLimit.countFailed'));
  for (const [type, n] of adding) {
    const kind = LOCAL_KIND_OF_VAULT_TYPE[type];
    const used = visibleItems(list).filter((it) => it?.type === type).length;
    if (!requireLocalCapacity(kind, used, { adding: n })) throw planLimitError(capRefusal(kind, used, n));
  }
}

/**
 * The in-flight `ensureUnlocked`, so overlapping callers share one round trip
 * instead of each issuing their own `vault_status` + `vault_unlock`.
 *
 * There are four independent unlock surfaces (this panel, the FTP sidebar,
 * `ftpStore`, the chat share card) and none coordinates with the others; CRA
 * runs the app inside `React.StrictMode`, so every `useEffect(…, [])` that
 * drives one of them fires twice on mount. Each duplicate used to reach Rust
 * before the first had resolved, so all of them read `unlocked: false` and all
 * of them unlocked.
 *
 * This is redundancy removal, not the prompt fix — after the Rust change a
 * redundant `vault_unlock` costs no keychain access at all. Cleared as soon as
 * the call settles, either way: caching a rejection would wedge the panel.
 *
 * Module-level, so it outlives the store's own state and `lockVault`/`reset`
 * have to drop it by hand. `reset()` runs on sign-out (`App.js`) while a
 * mount-time `ensureUnlocked` may still be awaiting three round trips; leaving
 * the promise behind hands the NEXT account's first caller the previous
 * session's result — including its `set({ status })` — without it ever issuing
 * a `vault_status` of its own.
 */
let unlockInflight = null;

/**
 * Abandon the in-flight unlock: whatever it resolves to describes a vault this
 * process is no longer in. The promise itself is not cancellable, but nothing
 * joins it once it is off this variable, and its own `finally` is a no-op
 * against a slot something else has already cleared.
 */
function dropUnlockInflight() {
  unlockInflight = null;
}

/**
 * The ONE frontend teardown for "this vault is now locked" (auto-lock plan A4).
 *
 * Three callers, deliberately sharing a single body so they cannot drift:
 *  - the manual Lock button (`lockVault`, which additionally issues the
 *    `vault_lock` invoke),
 *  - the App.js logout path,
 *  - the `vault:locked` Tauri event (Rust auto-lock / lock-on-suspend —
 *    subscribed in services/vaultActivity.js).
 *
 * Before this existed, a Rust-initiated lock would have been strictly weaker
 * than the button: the vault would report locked while the in-flight unlock
 * promise and the E2E chat-share private key both stayed live in JS.
 *
 * Idempotent by construction. NOT because every lock echoes an event — Rust
 * deliberately does not emit `vault:locked` for the manual `vault_lock`
 * command (the JS caller initiated it and runs this directly; see the doc on
 * that command). Only AUTONOMOUS locks emit: the idle sweeper and the
 * suspend/screen-lock hooks. Idempotency is still required because an
 * autonomous lock can race a manual one — sweeper fires, event arrives while
 * `lockVault` is mid-flight — so this body may legitimately run twice.
 * Corollary for the next editor: the manual path's direct call is
 * load-bearing; there is no event backstop behind it.
 *
 * `userLocked: true` on the event path is deliberate: an auto-lock must not be
 * undone by the silent keyring re-unlock that panel re-entry performs, or the
 * timeout is cosmetic on every vault that unlocks without a passphrase. Any
 * explicit unlock intent (`ensureUnlocked`) clears it, exactly as it clears it
 * after the manual button.
 */
/**
 * Every successful unlock runs through here, whichever route opened it.
 *
 * Deliberately idempotent and side-effect-free beyond the announcement: it is
 * called from paths that have already updated the store, so it must be safe to
 * call more than once for one unlock.
 */
export function onVaultUnlocked() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(VAULT_UNLOCKED_UI_EVENT));
}

export function onVaultLocked() {
  // An unlock still in flight describes a vault that is no longer unlocked;
  // nothing may join it and be handed a `status` that says otherwise.
  dropUnlockInflight();
  // Locking must actually remove chat-secret decryption capability, not just
  // flip a flag — the E2E private key lives in a module-level cache.
  resetE2eKeypairCache();
  useVaultStore.setState((s) => ({
    status: s.status
      ? { ...s.status, unlocked: false }
      : { initialized: true, unlocked: false, itemCount: 0 },
    items: [],
    selectedItemId: null,
    error: null,
    userLocked: true,
  }));
  // Last, after the store already answers "locked": listeners react by
  // re-reading, and reacting to this event before the state flipped would
  // cache the stale answer.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(VAULT_LOCKED_UI_EVENT));
  }
}

export const useVaultStore = create((set, get) => ({
  ...initialState,

  // A new category is a new list: the item open from the old one must not stay open
  // beside it (card 01b22bf7).
  setActiveCategory: (activeCategory) =>
    set((s) => (s.activeCategory === activeCategory ? {} : { activeCategory, selectedItemId: null })),
  setSearch: (search) => set({ search }),
  setSelectedItemId: (selectedItemId) => set({ selectedItemId }),

  /** Reload status + envelope list. Keeps the selection when it still exists. */
  refresh: async () => {
    if (!isTauri()) return;
    set({ loading: true, error: null });
    try {
      const [status, list] = await Promise.all([invoke('vault_status'), invoke('vault_list')]);
      const items = visibleItems(list);
      set((s) => ({
        status,
        items,
        loading: false,
        selectedItemId:
          s.selectedItemId && items.some((it) => it.id === s.selectedItemId)
            ? s.selectedItemId
            : null,
      }));
    } catch (e) {
      set({ loading: false, error: errText(e) });
    }
  },

  /**
   * Unlock (auto-initializing on first use) then load the list. Any call is an
   * explicit unlock intent, so it clears the userLocked flag. After a
   * successful unlock the account's E2E share keypair is created/published
   * (fire-and-forget) so opening the vault actually makes this account
   * reachable for encrypted chat shares.
   *
   * On a Protected vault with no usable device cache, `vault_unlock` answers
   * `passphrase_required` (CONTRACT §4.2 row C). That is not an error to paint
   * in the panel — it is a request for the passphrase — so it opens the gate
   * and leaves `error` clear. Every OTHER failure keeps today's behaviour
   * exactly, because turning an unrelated fault into an unlock prompt would
   * teach the user to retype their passphrase at a vault that is not asking.
   *
   * `vault_needs_bootstrap` rides the same route (vault-storage-transition
   * phase 5/7): a genuinely fresh scope (no `vault_meta` row, or one left
   * bare and empty by a pre-fix build) that `unlock_locked` now refuses
   * unconditionally rather than silently minting a keyring DEK for
   * (`vault.rs::resolve_dek`/`unlock_locked`). The gate resolves it into the
   * bootstrap ceremony (`vault_bootstrap_prepare`/`_commit`,
   * `VaultPassphraseGateHost`'s own routing) — the alternative, painting the
   * bare token in the panel, is the exact bug this phase exists to fix: a
   * fresh install could create a vault through no UI that existed. So is
   * `vault_kdf_corrupt`: a `kdf` block with no wraps at all cannot open with
   * ANY secret, so no oracle purpose is served by rendering the raw token
   * instead of the gate's own fixed, no-oracle failure copy
   * (`VaultPassphraseUnlockModal`'s doc comment already treats it the same
   * as `bad_passphrase` once inside the gate — this is what makes it reach
   * there in the first place).
   */
  ensureUnlocked: async () => {
    if (!isTauri()) return false;
    // Before the early return, not after: every call is an explicit unlock
    // intent, and a caller that joins an in-flight one must still clear
    // `userLocked` and light the button's spinner. Sharing the round trip is
    // an optimization; it must not change what the call MEANS.
    set({ loading: true, error: null, userLocked: false });
    if (unlockInflight) return unlockInflight;
    const run = async () => {
      try {
        let status = await invoke('vault_status');
        if (!status?.unlocked) status = await invoke('vault_unlock');
        set({ status });
        await get().refresh();
        if (status?.unlocked) {
          // Publishing the public key is safe to fail (retries next call) and
          // must never block the unlock flow.
          ensureE2eKeypair().catch(() => {});
          // Multi-device vault sync (PLAN.md §A2, call site 3): covers the
          // direct `vault_unlock`-with-no-secret fast path (the vault panel,
          // the FTP sidebar, the chat share card), which never dispatches
          // VAULT_GATE_UNLOCKED_EVENT and so would otherwise never reach the
          // gate's own `finish()` call site. Fire-and-forget, same as above.
          ensureVaultSync().catch(() => {});
          // The announcement the comment above describes as missing. Surfaces
          // that cache vault-derived state (the DB Manager guest above all)
          // learn about THIS path only from here — it is the one most unlocks
          // actually take.
          onVaultUnlocked();
        }
        return true;
      } catch (e) {
        // `vault_key_missing` rides the same route: it is not a fault to paint in
        // the panel either, it is the one state with a dedicated recovery screen,
        // and left to the generic handler it renders as an opaque token beside a
        // vault that lists every item and opens none of them.
        const token = vaultErrorToken(e);
        if (
          token === 'passphrase_required' ||
          token === 'vault_key_missing' ||
          token === 'vault_needs_bootstrap' ||
          token === 'vault_kdf_corrupt' ||
          // A keyblock this build cannot parse (not a newer version): same
          // route as corrupt. The bare token in the panel was a Sandbox finding.
          token === 'vault_kdf_unsupported' ||
          // Card 1.1: a vault from a newer Apiboo. The gate shows the update
          // screen; the bare token painted in the panel was the Sandbox finding.
          token === 'vault_update_required'
        ) {
          set({ loading: false, error: null });
          openVaultGate('vault');
          return false;
        }
        set({ loading: false, error: errText(e) });
        return false;
      }
    };
    // Compared by identity before clearing: a `lockVault` or `reset` that lands
    // mid-flight has already dropped this one and a later call may own the slot
    // by the time this settles.
    let mine;
    mine = run().finally(() => {
      if (unlockInflight === mine) unlockInflight = null;
    });
    unlockInflight = mine;
    return mine;
  },

  /**
   * Create or update an item ({ id?, type, name, sync?, favorite?, data }),
   * then refresh and select it. Errors propagate to the caller (the modal
   * shows them inline).
   */
  putItem: async (item) => {
    await guardNewVaultItems([item]);
    const envelope = await invoke('vault_put_item', { item });
    await get().refresh();
    set({ selectedItemId: envelope?.id || null });
    return envelope;
  },

  /** Tombstone-delete an item. Errors propagate to the caller. */
  deleteItem: async (id) => {
    await invoke('vault_delete_item', { id });
    set((s) => ({ selectedItemId: s.selectedItemId === id ? null : s.selectedItemId }));
    await get().refresh();
  },

  /** Drop the in-memory key Rust-side and clear the decrypted UI state. */
  lockVault: async () => {
    if (!isTauri()) return;
    // An unlock still in flight is now describing a vault the user has just
    // asked to close. Dropped before the `vault_lock` invoke, so nothing can
    // join it while the lock is on the wire — onVaultLocked drops it again
    // afterwards, which is free.
    dropUnlockInflight();
    try {
      await invoke('vault_lock');
    } catch (e) {
      console.warn('[vault] lock failed:', errText(e));
    }
    // Everything except the invoke is shared with the auto-lock event path —
    // see onVaultLocked. Adding cleanup HERE instead of there is how auto-lock
    // becomes weaker than the button again.
    onVaultLocked();
  },

  reset: () => {
    // Sign-out. The next signed-in account must not be handed the previous
    // one's unlock result — `initialState` cannot reach a module-level
    // variable, so this does it by hand.
    dropUnlockInflight();
    set({ ...initialState });
  },
}));

/**
 * Master-passphrase state (CONTRACT §4). A separate store from the item cache
 * on purpose: it is read by surfaces that never look at items (the sidebar
 * footer, the startup line, the gate) and it must survive `lockVault`, which
 * empties the item store.
 *
 * It holds NO secret. `vault_master_status` returns only the mode, the two
 * booleans, the epoch and a cost estimate for UI copy — never a wrap, never a
 * salt, never anything derived from the passphrase.
 */
const masterInitial = {
  // { mode: 'keyringOnly' | 'protected', unlocked, remembered, epoch,
  //   kdfMsEstimate, keyMissing, backupAvailable } | null when not yet read.
  //
  // `keyMissing` outranks `mode`: it reports `keyringOnly` for a file whose
  // wraps are absent while the keychain says they should not be, and in that
  // state the setup screen is the wrong answer.
  master: null,
  masterLoading: false,
};

export const useVaultMasterStore = create((set) => ({
  ...masterInitial,

  /**
   * Re-read `vault_master_status`. Never throws: every caller is a render path
   * and a failed status read must not take a panel down. A vault whose `kdf`
   * block is unreadable reports `protected` from Rust (`status_of` fails
   * closed), so a read error here leaves the previous value rather than
   * downgrading the UI to "no passphrase set".
   */
  refreshMaster: async () => {
    if (!isTauri()) return null;
    set({ masterLoading: true });
    try {
      const master = await invoke('vault_master_status');
      set({ master, masterLoading: false });
      return master;
    } catch (e) {
      console.warn('[vault] master status read failed:', errText(e));
      set({ masterLoading: false });
      return null;
    }
  },

  resetMaster: () => set({ ...masterInitial }),
}));

// resetMaster() had no production call site at all — every OTHER logout path
// (App.js's onLogoutReset, useVaultStore's own .reset()) covers the item
// cache, not this store, so `master`'s mode/unlocked/remembered flags simply
// described the departed account until some mounted vault surface's own
// mount-effect happened to call refreshVaultMaster() and overwrite it. This
// store holds no secret, but "describes the wrong account until something
// else happens to refresh it" is exactly the class of bug this signal exists
// to close. Registered once, unconditionally, at module load.
addSessionEndedListener(() => {
  useVaultMasterStore.getState().resetMaster();
});

export const refreshVaultMaster = () => useVaultMasterStore.getState().refreshMaster();

/** Selector: items narrowed by the active category + search text. */
export function selectFilteredItems(state) {
  // A TEAM category selects nothing from the personal list — explicitly, not
  // by accident. It already happened to return [] (no personal item's `type`
  // equals a `teamcat:…` string), but "happens to" is not a guarantee: it is
  // the reason a personal item can never appear under a team heading, so it
  // says so. Team items are rendered from `teamVaultStore` by the view; they
  // are never in `state.items` and must never be.
  if (isTeamCategory(state.activeCategory)) return [];
  const q = state.search.trim().toLowerCase();
  return state.items.filter((it) => {
    if (state.activeCategory === 'favorites') {
      if (!it.favorite) return false;
    } else if (typeof state.activeCategory === 'string' && state.activeCategory.startsWith(FOLDER_CATEGORY_PREFIX)) {
      // Folder categories are `folder:<name>`. Compared case-insensitively and
      // trimmed, matching how `selectFolders` collapses them into one entry —
      // otherwise clicking a folder could show fewer items than its own count.
      const want = state.activeCategory.slice(FOLDER_CATEGORY_PREFIX.length).trim().toLowerCase();
      const has = typeof it?.index?.folder === 'string' ? it.index.folder.trim().toLowerCase() : '';
      if (has !== want) return false;
    } else if (state.activeCategory !== 'all' && it.type !== state.activeCategory) {
      return false;
    }
    if (!q) return true;
    const haystack = [it.name, ...Object.values(it.index || {})]
      .filter((v) => typeof v === 'string' || typeof v === 'number')
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
}

/** Selector: per-category item counts for the sidebar. */
/**
 * Every folder name in use, sorted, case-insensitively de-duplicated.
 *
 * Derived from the PLAINTEXT index rather than from item bodies, so this costs
 * no decryption and works while the vault is listed but locked — the same
 * reason the sidebar can already show hosts and usernames.
 *
 * There is no folder registry: a folder exists exactly as long as something is
 * in it. That is deliberate for this pass — it needs no new synced entity, no
 * migration, and no orphan handling when the last item leaves. The cost is that
 * an empty folder does not persist, which is worth revisiting only if someone
 * actually wants to pre-create one.
 */
/** Sidebar rows for folders are `folder:<name>`; see selectFilteredItems. */
export const FOLDER_CATEGORY_PREFIX = 'folder:';

export function selectFolders(state) {
  const seen = new Map(); // lowercased -> first-seen original casing
  for (const it of state.items) {
    const f = typeof it?.index?.folder === 'string' ? it.index.folder.trim() : '';
    if (!f) continue;
    const key = f.toLowerCase();
    if (!seen.has(key)) seen.set(key, f);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

/**
 * How many items sit in each folder, keyed by the name `selectFolders` returns.
 *
 * IT MUST COLLAPSE CASE THE SAME WAY `selectFolders` DOES, and it did not: this
 * keyed on the trimmed name only, so items in "Work", "work" and "WORK" became
 * three separate counts while the sidebar drew ONE row. The row then showed the
 * count of whichever spelling happened to match its label — an undercount, and
 * the doc comment above has always claimed otherwise.
 *
 * Cosmetic until 06.08.2026, when folder DELETE started using this number as
 * the figure the user consents to: the dialog offered to delete "2 items", the
 * matcher (which does collapse case) destroyed 4. Two selectors disagreeing
 * about what a folder is, with a master-password prompt in between.
 *
 * First-seen casing agrees with `selectFolders` because both iterate
 * `state.items` in the same order.
 */
export function selectFolderCounts(state) {
  const counts = {};
  const canonical = new Map(); // lowercased -> first-seen original casing
  for (const it of state.items) {
    const f = typeof it?.index?.folder === 'string' ? it.index.folder.trim() : '';
    if (!f) continue;
    const key = f.toLowerCase();
    if (!canonical.has(key)) canonical.set(key, f);
    const name = canonical.get(key);
    counts[name] = (counts[name] || 0) + 1;
  }
  return counts;
}

export function selectCategoryCounts(state) {
  const counts = {
    all: state.items.length,
    favorites: 0,
    password: 0,
    sftp: 0,
    dbConnection: 0,
    authHeader: 0,
    note: 0,
    custom: 0,
  };
  for (const it of state.items) {
    if (it.favorite) counts.favorites += 1;
    if (counts[it.type] != null) counts[it.type] += 1;
  }
  return counts;
}

// Stable module-level setters/actions (callable without hooks).
export const setVaultActiveCategory = (v) => useVaultStore.getState().setActiveCategory(v);
export const setVaultSearch = (v) => useVaultStore.getState().setSearch(v);
export const setVaultSelectedItemId = (v) => useVaultStore.getState().setSelectedItemId(v);
export const refreshVault = () => useVaultStore.getState().refresh();
export const resetVaultStore = () => useVaultStore.getState().reset();
